/**
 * Phase 18/26 — Factory für Array-Aggregate (entity_* + Blob + domain_aggregate).
 */

import { isEntityStoreAuthorityEnabled } from '../infrastructure/storage/entityAggregateStore';
import { isEntityStoreNoKvBlobEnabled } from '../infrastructure/storage/businessPayloadMeta';
import { readBestBusinessMergedPayload } from './sharedCore';
import { readBlobFirstWithAggregateFallback } from './canonicalEntityRead';
import { readSyncWithBlobPriority } from './businessStateSync';

/**
 * @param {object} config
 * @param {string} config.field — Blob-Feldname (z. B. invoicePayments)
 * @param {() => Promise<object[]>} config.listAggregate
 * @param {() => Promise<object[]>} config.listEntity
 * @param {string[]} config.idKeys
 */
export function createArrayEntityReadSource({ field, listAggregate, listEntity, idKeys }) {
  /** @type {Map<string, object[]>} */
  const cache = new Map();

  function readFromMergedBlob(businessId) {
    if (!businessId) return [];
    if (isEntityStoreNoKvBlobEnabled() || isEntityStoreAuthorityEnabled()) {
      const cached = getCachedRead(businessId);
      if (cached) return cached;
    }
    if (isEntityStoreNoKvBlobEnabled()) return [];
    const { parsed } = readBestBusinessMergedPayload(businessId);
    return Array.isArray(parsed?.[field]) ? parsed[field] : [];
  }

  async function readFromBestSource(businessId) {
    return readBlobFirstWithAggregateFallback(
      businessId,
      readFromMergedBlob,
      listAggregate,
      listEntity,
      idKeys
    );
  }

  async function refreshReadCache(businessId, preloaded) {
    const items =
      preloaded !== undefined
        ? Array.isArray(preloaded)
          ? preloaded
          : []
        : await readFromBestSource(businessId);
    if (businessId) {
      cache.set(String(businessId), items);
    }
    return items;
  }

  function getCachedRead(businessId) {
    if (!businessId) return null;
    return cache.get(String(businessId)) ?? null;
  }

  function clearReadCache(businessId) {
    if (businessId) {
      cache.delete(String(businessId));
    } else {
      cache.clear();
    }
  }

  function readSyncForBusiness(businessId) {
    return readSyncWithBlobPriority(
      businessId,
      readFromMergedBlob,
      getCachedRead,
      ...idKeys
    );
  }

  return {
    readFromMergedBlob,
    readFromBestSource,
    refreshReadCache,
    getCachedRead,
    clearReadCache,
    readSyncForBusiness
  };
}
